import { createTheme, ThemeProvider } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions/transition";
import type { ComponentType, PropsWithChildren } from "react";
import type React from "react";
import { forwardRef } from "react";
import type { DesignSystemLinkHref, DesignSystemLinkProps } from "../routing/OctopusRoutingContext";
import { OctopusRoutingProvider } from "../routing/OctopusRoutingContext";
import { DefaultTransitionComponent } from "../transition/TransitionComponentProvider";
interface TestDesignSystemProviderProps {
    Link?: ReturnType<typeof React.forwardRef<HTMLAnchorElement, DesignSystemLinkProps>>;
    useIsUrlActive?: () => (path: DesignSystemLinkHref, exact: boolean | undefined) => boolean;
    useDispatchLinkAnalytics?: () => (linkLabel: string, href: string, name: string, linkLocation?: string) => void;
}
export function TestDesignSystemProvider({ Link = TestLink, useIsUrlActive = () => () => false, useDispatchLinkAnalytics = () => () => { }, children }: PropsWithChildren<TestDesignSystemProviderProps>) {
    return (<OctopusRoutingProvider Link={Link} useIsUrlActive={useIsUrlActive} useDispatchLinkAnalytics={useDispatchLinkAnalytics}>
            <SetMuiDefaultTransitions TransitionComponent={DefaultTransitionComponent}>{children}</SetMuiDefaultTransitions>
        </OctopusRoutingProvider>);
}
const TestLink = forwardRef<HTMLAnchorElement, DesignSystemLinkProps>(({ href, disableImplicitEventTracking, ...props }, ref) => {
    // Currently the design system needs to handle our intermediate PageUrl object
    // Here we just resolve it with no space id for tests
    const resolvedHref = href ? (typeof href === "string" ? href : href.resolveWithSpaceId(null)) : undefined;
    return <a {...props} href={resolvedHref} ref={ref}/>;
});
TestLink.displayName = "Link";
/**
 * Set the default transitions for MUI. This can be used to disable transitions
 * during tests for example.
 */
export const SetMuiDefaultTransitions = ({ children, TransitionComponent }: PropsWithChildren<{
    TransitionComponent: ComponentType<TransitionProps>;
}>) => {
    const theme = createTheme({
        transitions: {
            // Disable all transitions by overriding the mui transition helper
            create: () => "none",
        },
        components: {
            MuiDialog: {
                defaultProps: {
                    slots: {
                        transition: TransitionComponent,
                        backdrop: TransitionComponent,
                    },
                },
            },
            MuiDrawer: {
                defaultProps: {
                    slots: {
                        transition: TransitionComponent,
                        backdrop: TransitionComponent,
                    },
                },
            },
            MuiModal: {
                defaultProps: {
                    slots: {
                        backdrop: TransitionComponent,
                    },
                },
            },
            MuiSnackbar: {
                defaultProps: {
                    slots: {
                        transition: TransitionComponent,
                    },
                },
            },
            MuiPopover: {
                defaultProps: {
                    slots: {
                        transition: TransitionComponent,
                    },
                },
            },
        },
    });
    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
